/**
 * Browser Signal Collector
 * 
 * Lightweight utility that collects browser/headless automation signals
 * and sends them to the backend as supporting anti-cheat evidence.
 * 
 * IMPORTANT: These signals are secondary evidence only.
 * They must never be used alone to ban or block users.
 */

interface BrowserSignalPayload {
  webdriverDetected: boolean;
  consistencyIssues: string[];
  capabilityFlags: Record<string, boolean>;
  environmentNotes: string[];
  signalScore: number;
  collectedAt: string;
}

let cachedSignals: BrowserSignalPayload | null = null;
let lastCollectionTime = 0;
const COLLECTION_COOLDOWN_MS = 5 * 60 * 1000; // Only refresh every 5 minutes

/**
 * Collect lightweight browser signals.
 * Results are cached to avoid re-computation on every request.
 */
export function collectBrowserSignals(): BrowserSignalPayload {
  const now = Date.now();
  if (cachedSignals && (now - lastCollectionTime) < COLLECTION_COOLDOWN_MS) {
    return cachedSignals;
  }

  const consistencyIssues: string[] = [];
  const environmentNotes: string[] = [];
  const capabilityFlags: Record<string, boolean> = {};
  let signalScore = 0;

  // 1. Check navigator.webdriver
  const webdriverDetected = !!(navigator as any).webdriver;
  if (webdriverDetected) {
    signalScore += 3;
  }

  // 2. Check for missing/inconsistent browser APIs
  // These checks are conservative — absent APIs alone are not proof of automation
  const hasNotificationAPI = 'Notification' in window;
  const hasPermissionsAPI = 'permissions' in navigator;
  const hasServiceWorkerAPI = 'serviceWorker' in navigator;
  const hasWebGL = (() => {
    try {
      const canvas = document.createElement('canvas');
      return !!(canvas.getContext('webgl') || canvas.getContext('experimental-webgl'));
    } catch {
      return false;
    }
  })();

  capabilityFlags.notification = hasNotificationAPI;
  capabilityFlags.permissions = hasPermissionsAPI;
  capabilityFlags.serviceWorker = hasServiceWorkerAPI;
  capabilityFlags.webgl = hasWebGL;

  // Missing multiple common APIs can hint at a stripped environment
  const missingCommonAPIs = [
    !hasNotificationAPI && 'Notification',
    !hasWebGL && 'WebGL',
  ].filter(Boolean);

  if (missingCommonAPIs.length >= 2) {
    consistencyIssues.push(`Missing common APIs: ${missingCommonAPIs.join(', ')}`);
    signalScore += 1;
  }

  // 3. Check platform consistency
  const ua = navigator.userAgent || '';
  const platform = (navigator as any).platform || '';

  if (ua.includes('Win') && platform.includes('Linux')) {
    consistencyIssues.push('UA says Windows but platform says Linux');
    signalScore += 1;
  }
  if (ua.includes('Mac') && platform.includes('Win')) {
    consistencyIssues.push('UA says Mac but platform says Windows');
    signalScore += 1;
  }

  // 4. Check for unusual characteristics
  const languages = navigator.languages;
  if (!languages || languages.length === 0) {
    consistencyIssues.push('No languages defined');
    signalScore += 1;
  }

  // 5. Check chrome-specific automation flags
  if ((window as any).chrome) {
    if ((window as any).chrome.runtime === undefined && ua.includes('Chrome')) {
      // The chrome object exists but runtime is missing — could be headless Chrome
      // But this can also happen in iframes, so low weight
      environmentNotes.push('chrome object without runtime');
    }
  }

  // 6. Check for plugin inconsistencies
  const pluginCount = navigator.plugins?.length ?? 0;
  capabilityFlags.hasPlugins = pluginCount > 0;
  if (pluginCount === 0 && ua.includes('Chrome') && !ua.includes('Mobile')) {
    consistencyIssues.push('Chrome desktop with zero plugins');
    signalScore += 1;
  }

  // 7. Connection info
  const connection = (navigator as any).connection;
  if (connection) {
    environmentNotes.push(`connection: ${connection.effectiveType || 'unknown'}`);
  }

  // 8. Screen consistency
  if (window.screen) {
    const { width, height, colorDepth } = window.screen;
    capabilityFlags.normalScreen = width > 0 && height > 0 && colorDepth > 0;
    if (width === 0 && height === 0) {
      consistencyIssues.push('Zero screen dimensions');
      signalScore += 2;
    }
  }

  // Cap the score to avoid over-weighting browser signals
  signalScore = Math.min(signalScore, 10);

  cachedSignals = {
    webdriverDetected,
    consistencyIssues,
    capabilityFlags,
    environmentNotes,
    signalScore,
    collectedAt: new Date().toISOString(),
  };
  lastCollectionTime = now;

  return cachedSignals;
}

/**
 * Get the current cached signals (or collect fresh ones).
 * Returns null if signals haven't been collected yet.
 */
export function getCachedSignals(): BrowserSignalPayload | null {
  return cachedSignals;
}

/**
 * Force invalidate the cache (e.g. on new session).
 */
export function invalidateSignalCache(): void {
  cachedSignals = null;
  lastCollectionTime = 0;
}
